import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { SizeDataTypeInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getAdSpaceSizeDataTypesColumns } from './SizeDataTypesColumns';
import { AdSpaceSizeDataTypesUpdateDialog } from './SizeDataTypesUpdateDialog';

const AdSpaceSizeDataTypesAdminList: React.FC = () => {
    const [sizeDataTypes, setSizeDataTypes] = useState<SizeDataTypeInterface[]>([]);
    const [sizeDataType, setSizeDataType] = useState<SizeDataTypeInterface | null>(null);
    const [sizeDataTypeId, setSizeDataTypeId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [sizeDataTypeToDelete, setSizeDataTypeToDelete] = useState<SizeDataTypeInterface | null>(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [sizeDataTypeToUpdate, setSizeDataTypeToUpdate] = useState<SizeDataTypeInterface | null>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Fetch all size data types
    const handleGetAllSizeDataTypes = async () => {
        setError(null);
        setSizeDataType(null);
        setSuccess(null);
        try {
            const result = await apiGet<GenericResponseInterface<SizeDataTypeInterface[]>>('/sizeDataTypes');
            if (result.status && result.data) {
                setSizeDataTypes(result.data);
            } else {
                setError(result.message || 'Failed to fetch size data types');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch size data types');
        }
    };

    // Fetch size data type by id
    const handleGetSizeDataTypeById = async () => {
        setError(null);
        setSizeDataTypes([]);
        setSuccess(null);
        if (!sizeDataTypeId) return setError('Please enter a Size Data Type ID');
        try {
            const result = await apiGet<GenericResponseInterface<SizeDataTypeInterface>>(`/sizeDataTypes/${sizeDataTypeId}`);
            if (result.status && result.data) {
                setSizeDataType(result.data);
            } else {
                setError(result.message || 'Failed to fetch size data type');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch size data type');
        }
    };

    // Delete size data type
    const handleDeleteSizeDataType = async () => {
        if (!sizeDataTypeToDelete) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/sizeDataTypes/delete/${sizeDataTypeToDelete.id}`, 'DELETE');
            if (result.status) {
                setSuccess(result.message || 'Size data type deleted successfully');
                setDeleteDialogOpen(false);
                setSizeDataTypeToDelete(null);
                handleGetAllSizeDataTypes();
            } else {
                setError(result.message || 'Failed to delete size data type');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete size data type');
        }
    };

    // Update size data type
    const handleUpdateSizeDataType = async () => {
        if (!sizeDataTypeToUpdate || !sizeDataTypeToUpdate.id) return;
        setUpdateError(null);
        setSuccess(null);

        try {
            const result = await apiRequest<GenericResponseInterface<SizeDataTypeInterface>>(
                `/sizeDataTypes/update/${sizeDataTypeToUpdate.id}`,
                'PUT',
                sizeDataTypeToUpdate
            );
            if (result.status) {
                setSuccess(result.message || 'Size data type updated successfully');
                setUpdateDialogOpen(false);
                setSizeDataTypeToUpdate(null);
                handleGetAllSizeDataTypes();
            } else {
                setUpdateError(result.message || 'Failed to update size data type');
            }
        } catch (err: any) {
            setUpdateError(err.message || 'Failed to update size data type');
        }
    };

    const columns = getAdSpaceSizeDataTypesColumns(
        setSizeDataTypeToDelete,
        setDeleteDialogOpen,
        setSizeDataTypeToUpdate,
        setUpdateDialogOpen
    );

    useEffect(() => {
        handleGetAllSizeDataTypes();
    }, []);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetAllSizeDataTypes}>Get All Size Data Types</Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetSizeDataTypeById}>Get Size Data Type by ID</Button>
                <TextField
                    label="Size Data Type ID"
                    value={sizeDataTypeId}
                    onChange={e => setSizeDataTypeId(e.target.value)}
                    size="small"
                />
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                {sizeDataTypes.length > 0 && (
                    <DataGrid
                        rows={sizeDataTypes.map(a => ({ ...a, id: a.id ?? Math.random() }))}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } }
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                    />
                )}
                {sizeDataType && (
                    <DataGrid
                        rows={[{ ...sizeDataType, id: sizeDataType.id ?? Math.random() }]}
                        columns={columns}
                        hideFooter
                        autoHeight
                    />
                )}
            </Paper>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this size data type?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDeleteSizeDataType}>Delete</Button>
                </DialogActions>
            </Dialog>
            <AdSpaceSizeDataTypesUpdateDialog
                open={updateDialogOpen}
                sizeDataTypeToUpdate={sizeDataTypeToUpdate}
                setSizeDataTypeToUpdate={setSizeDataTypeToUpdate}
                onClose={() => {
                    setUpdateDialogOpen(false);
                    setSuccess(null);
                }}
                onSave={handleUpdateSizeDataType}
                updateError={updateError}
                updateSuccess={success}
            />
        </Box>
    );
};

export default AdSpaceSizeDataTypesAdminList;